import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGaugeHigh, faBuilding, faGear, faCreditCard,
  faBell, faBars, faXmark, faRightFromBracket
} from '@fortawesome/free-solid-svg-icons';

const navigation = [
  { name: 'Overview',     href: '/dashboard',              icon: faGaugeHigh  },
  { name: 'Subaccounts',  href: '/dashboard/subaccounts',  icon: faBuilding   },
  { name: 'Settings',     href: '/dashboard/settings',     icon: faGear       },
  { name: 'Billing',      href: '/dashboard/billing',      icon: faCreditCard },
];

const DashboardLayout = ({ children, userInfo, onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (href) =>
    href === '/dashboard'
      ? location.pathname === '/dashboard' || location.pathname === '/dashboard/'
      : location.pathname.startsWith(href);

  const handleNav = (href) => {
    setSidebarOpen(false);
    navigate(href);
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full" style={{ background: 'linear-gradient(180deg, #1e1b4b 0%, #312e81 100%)' }}>
      {/* Logo */}
      <div className="flex items-center gap-3 px-5 py-5 border-b" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
        <img src="/ghlwa.png" alt="GHLWA" className="h-9 w-auto" />
        <span className="text-xl font-extrabold text-white tracking-tight">GHLWA</span>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navigation.map((item) => {
          const active = isActive(item.href);
          return (
            <button
              key={item.name}
              onClick={() => handleNav(item.href)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150"
              style={active
                ? { borderLeft: '3px solid #6366f1', background: 'rgba(99,102,241,0.12)', color: '#ffffff' }
                : { borderLeft: '3px solid transparent', color: '#c7d2fe' }
              }
            >
              <FontAwesomeIcon icon={item.icon} className="w-4 h-4 flex-shrink-0" />
              <span>{item.name}</span>
            </button>
          );
        })}
      </nav>

      {/* User info at bottom */}
      <div className="px-4 py-4 border-t" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-bold text-white">
              {userInfo?.user?.firstName ? userInfo.user.firstName.charAt(0).toUpperCase() : 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">
              {userInfo?.user?.firstName ? `${userInfo.user.firstName} ${userInfo.user.lastName || ''}` : userInfo?.user?.email || 'User'}
            </p>
            <p className="text-xs text-indigo-300 truncate">{userInfo?.agency?.name || ''}</p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-screen flex overflow-hidden" style={{ background: '#f8fafc' }}>
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 flex md:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex-1 flex flex-col max-w-xs w-full z-50">
            <div className="absolute top-3 right-[-44px]">
              <button
                onClick={() => setSidebarOpen(false)}
                className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <SidebarContent />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="w-60">
          <SidebarContent />
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Top nav */}
        <header className="flex-shrink-0 flex items-center justify-between h-16 bg-white border-b border-secondary-100 px-4 shadow-sm">
          <div className="flex items-center gap-3">
            <button
              className="md:hidden p-2 rounded-lg text-secondary-500 hover:bg-secondary-100 transition-colors"
              onClick={() => setSidebarOpen(true)}
            >
              <FontAwesomeIcon icon={faBars} />
            </button>
            <h1 className="text-base font-semibold text-secondary-800">GHLWA Control Center</h1>
          </div>

          <div className="flex items-center gap-3">
            <button className="p-2 rounded-lg text-secondary-400 hover:bg-secondary-100 hover:text-secondary-600 transition-colors relative">
              <FontAwesomeIcon icon={faBell} />
            </button>

            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-white">
                  {userInfo?.user?.firstName ? userInfo.user.firstName.charAt(0).toUpperCase() : 'U'}
                </span>
              </div>
              <div className="hidden sm:block text-left">
                <p className="text-sm font-medium text-secondary-800 leading-tight">
                  {userInfo?.user?.firstName ? `${userInfo.user.firstName} ${userInfo.user.lastName || ''}` : userInfo?.user?.email || 'User'}
                </p>
                <p className="text-xs text-secondary-400 leading-tight">{userInfo?.agency?.name || ''}</p>
              </div>
              {onLogout && (
                <button
                  onClick={onLogout}
                  className="ml-1 flex items-center gap-1.5 text-sm font-medium text-secondary-500 hover:text-red-600 transition-colors px-2 py-1 rounded-lg hover:bg-red-50"
                >
                  <FontAwesomeIcon icon={faRightFromBracket} className="text-xs" />
                  <span className="hidden sm:inline">Logout</span>
                </button>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <div className="py-6 px-4 sm:px-6 max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
